import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, Stack } from '@mui/material';
import { Link } from 'react-router-dom';
import { privateAxiosInstance } from '../api/axios';
import DepartmentWidget from '../components/widgets/DepartmentWidget';
import LoadingComponent from '../components/widgets/LoadingComponent';
import styles from './AdvisorProfile.module.css';
import handleErrors from '../utils/errorHandler';

function AdvisorDetails({ advisor, setAdvisor, canEdit }) {
  const [isEditMode, setIsEditMode] = useState(false);
  const [editedResearchArea, setEditedResearchArea] = useState('');
  const [selectedDepartments, setSelectedDepartments] = useState([]);

  useEffect(() => {
    if (advisor) {
      setEditedResearchArea(advisor.research_area || '');
      setSelectedDepartments(
        advisor.departments ? advisor.departments.map((d) => d.name) : []
      );
    }
  }, [advisor]);

  if (!advisor) return <LoadingComponent />;

  const displayDepartments = (departments) => {
    if (departments && departments.length > 0) {
      return departments.map((d) => d.name).join(', ');
    }
    return 'Unknown';
  };

  const handleSave = async () => {
    const updatedFields = {};
    const deptString = selectedDepartments.join(', ');
    const advisorDeptString = advisor.departments
      .map((d) => d.name)
      .join(', ');
    if (deptString !== advisorDeptString) {
      updatedFields.department_names = selectedDepartments;
    }
    if (editedResearchArea !== (advisor.research_area || '')) {
      updatedFields.research_area = editedResearchArea;
    }
    if (Object.keys(updatedFields).length > 0) {
      try {
        const resp = await privateAxiosInstance.patch(
          `/api/v1/advisors/${advisor.id}/`,
          updatedFields
        );
        setAdvisor({ ...advisor, ...resp.data });
      } catch (error) {
        console.error('Error updating advisor details:', handleErrors(error));
      }
    }
    setIsEditMode(false);
  };

  const handleCancel = () => {
    setIsEditMode(false);
    setEditedResearchArea(advisor.research_area || '');
    setSelectedDepartments(
      advisor.departments ? advisor.departments.map((d) => d.name) : []
    );
  };

  return (
    <Box className={`page-container ${styles['advisor-container']}`}>
      <Box className={styles['title-bar']}>
        <Typography variant="h4" gutterBottom>
          {advisor.name}
        </Typography>
        {canEdit && (
          <Stack spacing={2} direction="row">
            {isEditMode ? (
              <>
                <Button variant="contained" onClick={handleSave} className={styles['save-btn']}>
                  Save
                </Button>
                <Button variant="outlined" onClick={handleCancel} className={styles['cancel-btn']}>
                  Cancel
                </Button>
              </>
            ) : (
              <Button variant="text" onClick={() => setIsEditMode(true)} className={styles['edit-btn']}>
                Edit Advisor
              </Button>
            )}
          </Stack>
        )}
      </Box>
      <Typography
        variant="h5"
        gutterBottom
        component={Link}
        to={`/school/${advisor.school}`}
        className={styles['school-link']}
      >
        <span className={styles['school-name']}>{advisor.school_name}</span>
      </Typography>
      <Typography variant="body1" gutterBottom style={{ paddingBottom: '2rem' }}>
        {isEditMode ? (
          <DepartmentWidget
            initialDepartments={selectedDepartments}
            onDepartmentsUpdated={setSelectedDepartments}
          />
        ) : (
          <>
            <b>Department: </b>
            {displayDepartments(advisor.departments)}
          </>
        )}
        <br />
        <b>Research Area: </b>
        {isEditMode ? (
          <input
            type="text"
            value={editedResearchArea}
            onChange={(e) => setEditedResearchArea(e.target.value)}
          />
        ) : (
          advisor.research_area || 'Unknown'
        )}
      </Typography>
    </Box>
  );
}

export default AdvisorDetails;
